import {
  CHART_ALL_FEEDBACK_ALIAS,
  CHART_FIELD,
  CHART_ROW_TOTAL_COLUMN_KEY,
  CHART_TOOLTIP_TARGET_TYPE,
  CHART_TOTAL_ROW_KEY,
} from '@/entities/chart/model/constants';
import chartUtils from '@/features/(charts)/chart-renderer/lib/chart-utils';
import type { CompareTotalsMap } from '@/features/(charts)/chart-renderer/ui/Cross/types';
import { calIncrementAmount } from '@/utils/util';

import {
  CellValueCalculator,
  SentimentCalculator,
  TotalCalculator,
} from '../calculators';
import { DataSourceGenerator } from './DataSourceGenerator';

/**
 * 비교 모드 데이터 소스 생성기
 * Previous/Current/%Change 데이터를 생성
 */
export class CompareDataGenerator extends DataSourceGenerator {
  /**
   * 비교 모드 데이터 소스 생성
   * - 각 행의 Previous/Current 데이터 계산
   * - %Change 계산
   * - showRowTotal이 true인 경우 Row Total Column 데이터 추가
   * - showColumnTotal이 true인 경우 Grand Total Row 추가
   */
  buildDataSource(): Record<string, StringNumberValue | null>[] {
    const {
      chart,
      configs,
      crossTableData,
      sortedCandidates,
      yHeaders,
      helpers,
    } = this.params;
    const primaryConfig = configs[0];

    const crossFieldType = chart.series[0]?.cross_field?.type;
    const isFeedbackAll = crossFieldType === CHART_FIELD.FEEDBACK_ALL;

    if (!primaryConfig) {
      throw new Error('Primary config is required');
    }

    const crossTable = crossTableData;
    const tableData = crossTable.tables[0] || [];
    const compareTableData = crossTable.compare_tables?.[0];

    // Calculate totals for both current and compare
    const currentTotals = this.calculateTotals(
      tableData,
      yHeaders,
      sortedCandidates,
      isFeedbackAll,
    );
    const compareTotals = compareTableData
      ? this.calculateTotals(
          compareTableData,
          yHeaders,
          sortedCandidates,
          isFeedbackAll,
        )
      : { yTotalMap: {}, xTotalMap: {}, grandTotal: 0 };

    const totals: CompareTotalsMap = {
      ...currentTotals,
      compareYTotalMap: compareTotals.yTotalMap,
      compareXTotalMap: compareTotals.xTotalMap,
      compareGrandTotal: compareTotals.grandTotal,
    };

    const result: Record<string, StringNumberValue | null>[] =
      sortedCandidates?.map((item) => {
        const crossFields: Record<string, number | null> = {};

        const currentData = tableData.find(
          (d: ChartTableXValue) => d.x === item,
        );
        const compareData = compareTableData?.find(
          (d: ChartTableXValue) => d.x === item,
        );

        yHeaders.forEach((alias) => {
          let aliasKey = alias;
          if (isFeedbackAll) {
            aliasKey = CHART_ALL_FEEDBACK_ALIAS;
          }
          crossFields[`${aliasKey}-${CHART_TOOLTIP_TARGET_TYPE.CURRENT}`] = 0;
          crossFields[`${aliasKey}-${CHART_TOOLTIP_TARGET_TYPE.COMPARE}`] = 0;
          crossFields[`${aliasKey}-change`] = 0;
        });

        // Calculate row totals
        let currentRowTotal = 0;
        let compareRowTotal = 0;

        currentData?.values?.forEach((y: ChartTableYValue) => {
          currentRowTotal += y.value;
          let aliasKey = y.y;
          if (isFeedbackAll) {
            aliasKey = CHART_ALL_FEEDBACK_ALIAS;
          }
          const totalForPercentage = TotalCalculator.getTotalForPercentage(
            primaryConfig.shareCalculationType,
            item,
            aliasKey,
            currentTotals,
          );
          crossFields[`${aliasKey}-${CHART_TOOLTIP_TARGET_TYPE.CURRENT}`] =
            CellValueCalculator.calculate(
              primaryConfig,
              y.value,
              totalForPercentage,
              y.values,
            );
        });

        compareData?.values?.forEach((y: ChartTableYValue) => {
          compareRowTotal += y.value;
          let aliasKey = y.y;
          if (isFeedbackAll) {
            aliasKey = CHART_ALL_FEEDBACK_ALIAS;
          }
          const totalForPercentage = TotalCalculator.getTotalForPercentage(
            primaryConfig.shareCalculationType,
            item,
            aliasKey,
            compareTotals,
          );
          crossFields[`${aliasKey}-${CHART_TOOLTIP_TARGET_TYPE.COMPARE}`] =
            CellValueCalculator.calculate(
              primaryConfig,
              y.value,
              totalForPercentage,
              y.values,
            );
        });

        // Add row totals if needed (Row Total Column)
        if (primaryConfig.showRowTotal) {
          crossFields[
            `${CHART_ROW_TOTAL_COLUMN_KEY}-${CHART_TOOLTIP_TARGET_TYPE.CURRENT}`
          ] = TotalCalculator.calculateRowTotalValue(
            primaryConfig,
            currentRowTotal,
            totals.grandTotal,
          );
          crossFields[
            `${CHART_ROW_TOTAL_COLUMN_KEY}-${CHART_TOOLTIP_TARGET_TYPE.COMPARE}`
          ] = TotalCalculator.calculateRowTotalValue(
            primaryConfig,
            compareRowTotal,
            totals.compareGrandTotal,
          );
        }

        // Calculate changes
        yHeaders.forEach((alias) => {
          let aliasKey = alias;
          if (isFeedbackAll) {
            aliasKey = CHART_ALL_FEEDBACK_ALIAS;
          }

          const current =
            crossFields[`${aliasKey}-${CHART_TOOLTIP_TARGET_TYPE.CURRENT}`];
          const prev =
            crossFields[`${aliasKey}-${CHART_TOOLTIP_TARGET_TYPE.COMPARE}`];
          crossFields[`${aliasKey}-change`] = calIncrementAmount(
            current || 0,
            prev || 0,
          );
        });

        if (primaryConfig.showRowTotal) {
          const currentRowTotalValue =
            crossFields[
              `${CHART_ROW_TOTAL_COLUMN_KEY}-${CHART_TOOLTIP_TARGET_TYPE.CURRENT}`
            ];
          const compareRowTotalValue =
            crossFields[
              `${CHART_ROW_TOTAL_COLUMN_KEY}-${CHART_TOOLTIP_TARGET_TYPE.COMPARE}`
            ];
          crossFields[`${CHART_ROW_TOTAL_COLUMN_KEY}-change`] =
            calIncrementAmount(
              currentRowTotalValue || 0,
              compareRowTotalValue || 0,
            );
        }

        const levelRowMap = helpers.getLevelRowMap(item);
        return {
          key: item,
          ...levelRowMap,
          ...crossFields,
        };
      }) || [];

    // Add Grand Total Row if needed (하단 합계 행)
    if (primaryConfig.showColumnTotal) {
      const fields = chartUtils.getKeyValueMapByKeys(yHeaders);
      const compareFields = chartUtils.getKeyValueMapByKeys(yHeaders);
      const columnTotalFields = chartUtils.getKeyValueMapByKeys(yHeaders);

      Object.keys(totals.yTotalMap).forEach((alias) => {
        if (primaryConfig.isCellSentimentCalculation) {
          fields[`${alias}-${CHART_TOOLTIP_TARGET_TYPE.CURRENT}`] = Number(
            SentimentCalculator.calculateTotalSentimentPercentage(
              alias,
              tableData,
              primaryConfig.sentimentSegment,
            ).toFixed(2),
          );
        } else {
          fields[`${alias}-${CHART_TOOLTIP_TARGET_TYPE.CURRENT}`] =
            TotalCalculator.calculateColumnTotalValue(
              primaryConfig,
              totals.yTotalMap[alias],
              totals.grandTotal,
            );
        }
      });

      Object.keys(totals.compareYTotalMap).forEach((alias) => {
        if (primaryConfig.isCellSentimentCalculation && compareTableData) {
          compareFields[`${alias}-${CHART_TOOLTIP_TARGET_TYPE.COMPARE}`] =
            Number(
              SentimentCalculator.calculateTotalSentimentPercentage(
                alias,
                compareTableData,
                primaryConfig.sentimentSegment,
              ).toFixed(2),
            );
        } else {
          compareFields[`${alias}-${CHART_TOOLTIP_TARGET_TYPE.COMPARE}`] =
            TotalCalculator.calculateColumnTotalValue(
              primaryConfig,
              totals.compareYTotalMap[alias],
              totals.compareGrandTotal,
            );
        }
      });

      // Calculate changes for each column
      Object.keys(totals.compareYTotalMap).forEach((alias) => {
        let aliasKey = alias;
        if (isFeedbackAll) {
          aliasKey = CHART_ALL_FEEDBACK_ALIAS;
        }
        const currentTotal =
          fields[`${aliasKey}-${CHART_TOOLTIP_TARGET_TYPE.CURRENT}`] || 0;
        const compareTotal =
          compareFields[`${aliasKey}-${CHART_TOOLTIP_TARGET_TYPE.COMPARE}`] ||
          0;

        columnTotalFields[`${aliasKey}-change`] =
          calIncrementAmount(currentTotal, compareTotal) || 0;
      });

      // Add Row Total Column in Grand Total Row (show_row_total이 true인 경우만)
      if (primaryConfig.showRowTotal) {
        fields[
          `${CHART_ROW_TOTAL_COLUMN_KEY}-${CHART_TOOLTIP_TARGET_TYPE.CURRENT}`
        ] = TotalCalculator.calculateGrandTotalValue(
          primaryConfig,
          totals.grandTotal,
        );
        compareFields[
          `${CHART_ROW_TOTAL_COLUMN_KEY}-${CHART_TOOLTIP_TARGET_TYPE.COMPARE}`
        ] = TotalCalculator.calculateGrandTotalValue(
          primaryConfig,
          totals.compareGrandTotal,
        );
        columnTotalFields[`${CHART_ROW_TOTAL_COLUMN_KEY}-change`] = 0;
      }

      result.push({
        key: CHART_TOTAL_ROW_KEY,
        ...helpers.getTotalRowMap(),
        ...fields,
        ...compareFields,
        ...columnTotalFields,
      });
    }

    return result;
  }
}
